/** 项目收款更正弹窗：保存历史首笔到账资料，并在提交前预览原付款期的剩余应收。
 * 签约合同的差额单只拆分收款展示，合同应收与抵扣仍以原期次为准；旧合同可能没有系统发票，不能在提示中虚构票号。 */
import React, { useRef, useState } from "react";
import { FilePenLine, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { useApp } from "../../context/AppContext";
import { errText } from "../../lib/detailText";

const METHODS = ["Check 支票", "Wire 电汇", "Zelle", "Cash 现金", "Credit Card 信用卡", "Other 其他"];
const inputClass = "mt-1 w-full min-h-11 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60";
const amountText = (value) => Number(value || 0).toFixed(2);
const initialReceipt = (payment) => ({
  date: String(payment.paidAt || "").slice(0, 10), method: payment.method || "",
  reference: payment.reference || "", paidAmount: amountText(payment.paidAmount ?? payment.receivedAmount ?? payment.amount),
  payee: payment.payee || "", payerName: payment.payerName || "", note: payment.payNote || "",
});
const EDITABLE = ["date", "method", "reference", "paidAmount", "payee", "payerName", "note"];

/** 更正已收款的历史日期及金额，同时同步统计；旧记录可保留未登记的付款方式，避免臆填。
 * 原值快照防止覆盖他人更正；按原合同期次净额预览首次生成或更新差额单，不能拿首笔展示金额当整期应收。 */
export function ReceiptMetadataDialog({ api, pid, payment, stageLabel, zh, onClose, onSaved, onConflict }) {
  const { cashMode } = useApp();
  const [original] = useState(() => initialReceipt(payment));
  const [form, setForm] = useState(original);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const saving = useRef(false);
  const availableMethods = cashMode?.enabled === false ? METHODS.filter((value) => !/cash/i.test(value)) : METHODS;
  const methods = original.method && !availableMethods.includes(original.method) ? [original.method, ...availableMethods] : availableMethods;
  const changed = EDITABLE.some((key) => key === "paidAmount"
    ? amountText(form[key]) !== amountText(original[key])
    : String(form[key] || "") !== String(original[key] || ""));
  // 已有签约差额单时，payment.amount 是首笔展示金额，不是原合同期次金额。
  // 以原期次金额扣除已有抵扣，按分计算新实收后的余额；旧合同可能未在系统建立原发票。
  // 只在实收金额变化时预览；已有差额调到零也需要提示撤销未使用补单的效果。
  const receivedAmount = Number(form.paidAmount);
  const validPreviewAmount = form.paidAmount !== "" && Number.isFinite(receivedAmount) && receivedAmount >= 0;
  const invoiceFaceCents = Math.max(0, Math.round(Number(payment.invoiceFaceAmount ?? payment.amount ?? 0) * 100) || 0);
  const creditCents = Math.min(invoiceFaceCents, Math.max(0, Math.round(Number(payment.creditTotal || 0) * 100) || 0));
  const shortfallCents = validPreviewAmount
    ? Math.max(0, invoiceFaceCents - creditCents - Math.round(receivedAmount * 100)) : 0;
  const previewShortfall = validPreviewAmount && amountText(form.paidAmount) !== amountText(original.paidAmount)
    && (shortfallCents > 0 || payment.shortfallSplit);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (saving.current || !changed || conflict) return;
    if (!form.date || form.paidAmount === "" || !Number.isFinite(Number(form.paidAmount)) || Number(form.paidAmount) < 0) {
      setError(zh ? "请填写有效的收款日期和实收金额" : "Enter a valid receipt date and received amount.");
      return;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      const payload = { ...form };
      EDITABLE.forEach((key) => { payload[`expected${key[0].toUpperCase()}${key.slice(1)}`] = original[key]; });
      await api.patch(`/finance/project/${pid}/payments/${encodeURIComponent(payment.stage)}/receipt`, payload);
      toast.success(zh ? "收款信息已更正，实收统计已同步" : "Receipt details corrected and collected totals updated");
      onSaved();
    } catch (e) {
      const detail = errText(e);
      // 409 既用于并发快照过期，也用于“历史原发票未建立”等财务业务门禁。
      // 旧逻辑把所有 409 都误报成他人更新并锁死表单，财务看不到真正卡点；
      // 仅合同收款 CAS 冲突要求重开；“原期发票状态已变化”是需财务核对的
      // 票据业务冲突，不能只因含“已变化”就盖掉具体原因并锁死用户草稿。
      const stale = e?.response?.status === 409 && (!e?.response?.data?.detail
        || /收款记录已被更新，请刷新后重新编辑|^This receipt has changed\b/i.test(detail));
      if (stale) {
        setConflict(true);
        setError(zh ? "这笔收款已被更新，请关闭后重新打开，核对最新信息。" : "This receipt has changed. Close and reopen to review the latest details.");
        onConflict?.();
      } else {
        // 历史旧账的发票缺失是真实财务门禁，不可用英文通用“并发冲突”掩盖；
        // 后端旧错误仍是中文，英文界面先解释可执行的核对动作，未知中文原因保留原文供财务追查。
        const explanation = !zh && /原合同付款期尚无发票/.test(detail)
          ? "The original contract installment has no system invoice. Finance must verify the original invoice and receipt before creating a balance collection."
          : !zh && /[\u4e00-\u9fff]/.test(detail)
            ? `Unable to save this receipt. Ask Finance to review: ${detail}` : detail;
        setError(explanation || (zh ? "保存失败，请重试" : "Unable to save. Please retry."));
      }
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const textField = (key, labelZh, labelEn, testId, props = {}) => <label key={key} className="block text-xs font-medium text-slate-600">
    {zh ? labelZh : labelEn}
    <input value={form[key]} onChange={(e) => set(key, e.target.value)} disabled={busy || conflict} className={inputClass} data-testid={testId} {...props} />
  </label>;

  return <Dialog open onOpenChange={(open) => { if (!open && !saving.current) onClose(); }}>
    <DialogContent className="max-w-xl" closeLabel={zh ? "关闭" : "Close"} closeDisabled={busy}
      data-testid="receipt-metadata-dialog"
      onEscapeKeyDown={(e) => { if (saving.current) e.preventDefault(); }}
      onInteractOutside={(e) => { if (saving.current) e.preventDefault(); }}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><FilePenLine className="h-5 w-5 shrink-0 text-brand-600" />{zh ? "编辑收款信息" : "Edit receipt details"}</DialogTitle>
        <DialogDescription>{stageLabel}</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-4">
        <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500">{zh ? "可更正公司、付款方、凭证编号、日期、实收金额、付款方式和备注。实收金额会同步财务统计；原计划金额和收款记录保留，更正均留有记录。" : "Correct company, payer, reference, date, amount received, payment method and note. Received totals update in finance; the original schedule and receipt remain in the audit trail."}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {textField("payee", "公司 / 收款方", "Company / payee", "receipt-edit-payee", { autoComplete: "organization" })}
          {textField("payerName", "付款方", "Payer", "receipt-edit-payer", { autoComplete: "name" })}
          {textField("reference", "凭证编号 / 支票号", "Receipt / check reference", "receipt-edit-reference")}
          {textField("paidAmount", "实收金额 ($) *", "Amount received ($) *", "receipt-edit-amount", { type: "number", min: "0", step: "0.01", required: true, inputMode: "decimal" })}
          {textField("date", "收款日期 *", "Receipt date *", "receipt-edit-date", { type: "date", required: true, autoFocus: true })}
          <label className="block text-xs font-medium text-slate-600">{zh ? "付款方式" : "Payment method"}
            <select value={form.method} onChange={(e) => set("method", e.target.value)} disabled={busy || conflict} className={inputClass} data-testid="receipt-edit-method">
              {!original.method && <option value="">{zh ? "未登记（可保持为空）" : "Not recorded (may remain blank)"}</option>}
              {methods.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-slate-600 sm:col-span-2">{zh ? "备注 / 付款说明" : "Note / payment details"}
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} disabled={busy || conflict} rows={3} maxLength={200} className={`${inputClass} min-h-20`} data-testid="receipt-edit-note" />
          </label>
        </div>
        {previewShortfall && <p role="status" data-testid="receipt-edit-shortfall" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
          {payment.shortfallSplit
            ? shortfallCents > 0
              ? (zh ? `按原付款期扣除抵扣后的净额计算，待补收差额 $${(shortfallCents / 100).toFixed(2)}。保存后将更新已有差额补收单；原合同条款、到期日和已有发票信息不变。若补收单已有到账或凭证，系统会拒绝自动调额。` : `Based on the original installment net of credits, the balance due is $${(shortfallCents / 100).toFixed(2)}. Saving updates the existing balance collection without changing contract terms, due date, or any existing invoice. A used supplement cannot be resized automatically.`)
              : (zh ? "按原付款期净额计算，差额已清零。保存后将撤销未使用的差额补收单；若已有到账或凭证，系统会拒绝自动撤销。原合同条款与已有发票信息不变。" : "The original installment's net balance is now zero. Saving removes an unused balance collection; a supplement with receipts or proofs cannot be removed automatically. Contract terms and any existing invoice stay unchanged.")
            : (zh ? `本期实收少于计划 $${(shortfallCents / 100).toFixed(2)}。保存后将在本期后生成差额补收单，后续期次显示编号顺延；原计划金额和日期不变。` : `This receipt is $${(shortfallCents / 100).toFixed(2)} below the planned installment. Saving adds a balance collection after this installment and shifts later displayed numbers; the original plan and dates remain unchanged.`)}
        </p>}
        {error && <p role="alert" className="text-sm text-red-600" data-testid="receipt-edit-error">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="receipt-edit-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button type="submit" disabled={busy || conflict || !changed || !form.date || form.paidAmount === ""} data-testid="receipt-edit-save">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存更正" : "Save changes")}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
